import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'
import { getSocket } from '../socket/client.js'

/**
 * Tenant-wide conversation analytics (GET /agent/dashboard/conversations) that
 * powers the Conversation Dashboard page. Driven by the date range and
 * refreshed live on any conversation change (conversation:list:bump).
 */
export interface PriorityCount {
  label: string
  color: string
  count: number
}

export interface TrendPoint {
  day: string
  opened: number
  resolved: number
}

export interface RecentConversation {
  referenceId: string
  subject: string | null
  channel: string
  status: string
  openedAt: string | null
  lastMessageAt: string | null
  csatScore: number | null
  priority: string
  priorityColor: string
  slaBreached: boolean
  displayName: string
}

export interface ConversationDashboardSummary {
  statusCounts: { open: number; closed: number; on_hold: number; reopen: number }
  total: number
  unread: number
  starred: number
  archived: number
  sla: {
    avgResponseHours: number
    avgResolutionHours: number
    breached: number
    met: number
    tracked: number
    compliancePct: number
  }
  csat: { avg: number; count: number }
  channelCounts: Record<string, number>
  sourceCounts: Record<string, number>
  priorityCounts: PriorityCount[]
  categoryCounts: { label: string; count: number }[]
  subcategoryCounts: { label: string; count: number }[]
  trend: TrendPoint[]
  recent: RecentConversation[]
  range: { from: string | null; to: string | null }
}

const EMPTY: ConversationDashboardSummary = {
  statusCounts: { open: 0, closed: 0, on_hold: 0, reopen: 0 },
  total: 0,
  unread: 0,
  starred: 0,
  archived: 0,
  sla: { avgResponseHours: 0, avgResolutionHours: 0, breached: 0, met: 0, tracked: 0, compliancePct: 100 },
  csat: { avg: 0, count: 0 },
  channelCounts: {},
  sourceCounts: {},
  priorityCounts: [],
  categoryCounts: [],
  subcategoryCounts: [],
  trend: [],
  recent: [],
  range: { from: '', to: '' },
}

export interface UseConversationDashboardResult {
  data: ConversationDashboardSummary
  loading: boolean
  refetch: () => Promise<void>
}

export function useConversationDashboard(from: string, to: string, statuses?: string[]): UseConversationDashboardResult {
  const [data, setData] = useState<ConversationDashboardSummary>(EMPTY)
  const [loading, setLoading] = useState(true)
  // Joined once so a rebuilt array doesn't restart the fetch loop.
  const statusKey = statuses?.join(',')

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true)
    try {
      const p = new URLSearchParams()
      if (from) p.set('from', from)
      if (to) p.set('to', to)
      if (statusKey !== undefined) p.set('statuses', statusKey)
      const res = await api.get<ConversationDashboardSummary>(`/agent/dashboard/conversations?${p.toString()}`)
      setData(res)
    } catch {
      setData(EMPTY)
    } finally {
      setLoading(false)
    }
  }, [from, to, statusKey])

  useEffect(() => { void refetch() }, [refetch])

  // Realtime: the tenant room bumps `conversation:list:bump` on any create /
  // update / status change — debounce-refresh so the dashboard stays live.
  useEffect(() => {
    const socket = getSocket()
    let t: ReturnType<typeof setTimeout> | null = null
    const onBump = (): void => {
      if (t) clearTimeout(t)
      t = setTimeout(() => void refetch(), 600)
    }
    socket.on('conversation:list:bump', onBump)
    return () => {
      if (t) clearTimeout(t)
      socket.off('conversation:list:bump', onBump)
    }
  }, [refetch])

  return { data, loading, refetch }
}
